import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-adapter-date-fns';
import { EiaForecastData } from '@rti/shared';
import { getRsColors } from '@/utils/chartColors';
import { useTheme } from '@/contexts/ThemeContext';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';

Chart.register(...registerables, ChartDataLabels);

interface EiaForecastChartProps {
  eiaData: EiaForecastData[];
}

const EiaForecastChart: React.FC<EiaForecastChartProps> = ({ eiaData }) => {
  const { isDark } = useTheme();
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const isCreating = useRef(false);
  const hoveredRef = useRef<{ datasetIndex: number; index: number } | null>(null);

  const rsColors = useMemo(() => getRsColors(), []);
  const tickColor = isDark ? '#e9ede6' : '#1e2019';
  const gridColor = isDark ? '#566151' : '#d0d3ce';

  // Detect small screens using the actual container width instead of window width
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const isMobile = (containerWidth ?? 0) > 0 ? containerWidth! <= 640 : (typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false);

  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) return;
    const container = canvas.parentElement || canvas;

    // Initial measurement
    const measure = () => {
      try {
        const rect = container.getBoundingClientRect();
        if (rect && rect.width) setContainerWidth(Math.round(rect.width));
      } catch {}
    };
    measure();

    // Prefer ResizeObserver when available
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = (entry as any).contentBoxSize
            ? Array.isArray((entry as any).contentBoxSize)
              ? (entry as any).contentBoxSize[0].inlineSize
              : (entry as any).contentBoxSize.inlineSize
            : entry.contentRect.width;
          setContainerWidth(Math.round(width));
        }
      });
      ro.observe(container);
      return () => ro.disconnect();
    }

    // Fallback: window resize listener
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const processedData = useMemo(() => {
    if (!eiaData || eiaData.length === 0) return null;

    const sortedData = [...eiaData].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const historicalData = sortedData.filter(item => {
      return item.year < currentYear || (item.year === currentYear && item.month <= currentMonth);
    });

    const forecastData = sortedData.filter(item => {
      return item.year > currentYear || (item.year === currentYear && item.month > currentMonth);
    });

    // Style tweaks for mobile
    const pointRadius = isMobile ? 0 : 4;
    const pointHoverRadius = isMobile ? 0 : 8;
    const borderWidth = isMobile ? 2 : 3;

    const datasets: any[] = [];

    if (historicalData.length > 0) {
      datasets.push({
        label: 'Brent Crude Oil',
        data: historicalData.map(item => ({
          x: item.date,
          y: item.brent_crude_oil_spot_price
        })),
        borderColor: rsColors.darkGreen,
        backgroundColor: 'transparent',
        borderWidth,
        fill: false,
        tension: 0.2,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: rsColors.darkGreen,
        pointBorderColor: rsColors.darkGreen,
        pointHoverBackgroundColor: rsColors.darkGreen,
        pointHoverBorderColor: rsColors.offWhite,
        pointHoverBorderWidth: 2,
        borderDash: [],
      });
    }

    if (forecastData.length > 0) {
      const connectPoint = historicalData.length > 0 ? [historicalData[historicalData.length - 1]] : [];
      datasets.push({
        label: 'Brent Crude Oil',
        data: [...connectPoint, ...forecastData].map(item => ({
          x: item.date,
          y: item.brent_crude_oil_spot_price
        })),
        borderColor: rsColors.darkGreen,
        backgroundColor: 'transparent',
        borderWidth,
        fill: false,
        tension: 0.2,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: rsColors.darkGreen,
        pointBorderColor: rsColors.darkGreen,
        pointHoverBackgroundColor: rsColors.darkGreen,
        pointHoverBorderColor: rsColors.offWhite,
        pointHoverBorderWidth: 2,
        borderDash: [10, 5],
      });
    }

    if (historicalData.length > 0) {
      datasets.push({
        label: 'WTI Crude Oil',
        data: historicalData.map(item => ({
          x: item.date,
          y: item.wti_crude_oil_spot_price
        })),
        borderColor: rsColors.purple,
        backgroundColor: 'transparent',
        borderWidth,
        fill: false,
        tension: 0.2,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: rsColors.purple,
        pointBorderColor: rsColors.purple,
        pointHoverBackgroundColor: rsColors.purple,
        pointHoverBorderColor: rsColors.offWhite,
        pointHoverBorderWidth: 2,
        borderDash: [],
      });
    }

    if (forecastData.length > 0) {
      const connectPoint = historicalData.length > 0 ? [historicalData[historicalData.length - 1]] : [];
      datasets.push({
        label: 'WTI Crude Oil',
        data: [...connectPoint, ...forecastData].map(item => ({
          x: item.date,
          y: item.wti_crude_oil_spot_price
        })),
        borderColor: rsColors.purple,
        backgroundColor: 'transparent',
        borderWidth,
        fill: false,
        tension: 0.2,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: rsColors.purple,
        pointBorderColor: rsColors.purple,
        pointHoverBackgroundColor: rsColors.purple,
        pointHoverBorderColor: rsColors.offWhite,
        pointHoverBorderWidth: 2,
        borderDash: [10, 5],
      });
    }

    if (historicalData.length > 0) {
      datasets.push({
        label: 'Natural Gas Henry Hub',
        data: historicalData.map(item => ({
          x: item.date,
          y: item.natural_gas_henry_hub_price_usd
        })),
        borderColor: rsColors.salmon,
        backgroundColor: 'transparent',
        borderWidth,
        fill: false,
        tension: 0.2,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: rsColors.salmon,
        pointBorderColor: rsColors.salmon,
        pointHoverBackgroundColor: rsColors.salmon,
        pointHoverBorderColor: rsColors.offWhite,
        pointHoverBorderWidth: 2,
        borderDash: [],
        yAxisID: 'y1',
      });
    }

    if (forecastData.length > 0) {
      const connectPoint = historicalData.length > 0 ? [historicalData[historicalData.length - 1]] : [];
      datasets.push({
        label: 'Natural Gas Henry Hub',
        data: [...connectPoint, ...forecastData].map(item => ({
          x: item.date,
          y: item.natural_gas_henry_hub_price_usd
        })),
        borderColor: rsColors.salmon,
        backgroundColor: 'transparent',
        borderWidth,
        fill: false,
        tension: 0.2,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: rsColors.salmon,
        pointBorderColor: rsColors.salmon,
        pointHoverBackgroundColor: rsColors.salmon,
        pointHoverBorderColor: rsColors.offWhite,
        pointHoverBorderWidth: 2,
        borderDash: [10, 5],
        yAxisID: 'y1',
      });
    }

    return { datasets };
  }, [eiaData, rsColors, isMobile, isDark]);

  useEffect(() => {
    if (!chartRef.current || !processedData || isCreating.current) return;

    isCreating.current = true;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) {
      isCreating.current = false;
      return;
    }

    const config: ChartConfiguration = {
      type: 'line',
      data: processedData,
      plugins: [createVerticalHoverLinePlugin()],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: isMobile ? 'bottom' : 'top',
            labels: {
              color: tickColor,
              font: { size: isMobile ? 10 : 12 },
              usePointStyle: true,
              pointStyle: 'line',
              filter: (legendItem, chartData) => {
                const label = legendItem.text;
                const existingLabels = chartData.datasets
                  .slice(0, legendItem.datasetIndex)
                  .map(dataset => dataset.label);
                return !existingLabels.includes(label);
              }
            },
            onClick: (e, legendItem, legend) => {
              const chart = legend.chart;
              const clickedLabel = legendItem.text;

              // Find all datasets with the same label (both historical and forecast)
              chart.data.datasets.forEach((dataset, index) => {
                if (dataset.label === clickedLabel) {
                  const meta = chart.getDatasetMeta(index);
                  meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : !meta.hidden;
                }
              });

              chart.update();
            }
          },
          tooltip: {
            enabled: false,
            external: () => null,
            intersect: false,
            filter: () => false
          },
          datalabels: {
            display: !isMobile,
            font: (context: any) => {
              const hovered = hoveredRef.current;
              const isHovered = !!hovered &&
                hovered.datasetIndex === context.datasetIndex &&
                hovered.index === context.dataIndex;
              const baseSize = isMobile ? 11 : 15;
              return {
                size: isHovered ? baseSize * 2 : baseSize,
                weight: isHovered ? 'bold' : 'normal',
              };
            },
            anchor: (context: any) => context.dataIndex % 2 === 0 ? 'end' : 'start',
            align: (context: any) => context.dataIndex % 2 === 0 ? 'top' : 'bottom',
            offset: isMobile ? 4 : 6,
            color: (context) => {
              // Removed unused datasetIndex
              const label = context.dataset.label || '';

              if (label.includes('Brent')) {
                return rsColors.darkGreen;
              } else if (label.includes('WTI')) {
                return rsColors.purple;
              } else if (label.includes('Natural Gas')) {
                return rsColors.salmon;
              }
              return tickColor;
            },
            formatter: (value) => {
              const numericValue = typeof value === 'number' ? value : (typeof value === 'object' && (value as any)?.y ? (value as any).y : parseFloat(value as any));
              return numericValue && !isNaN(numericValue) ? '$' + (numericValue as number).toFixed(2) : '';
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: isMobile ? 'MMM yy' : 'MMM yyyy'
              }
            },
            grid: {
              color: gridColor,
              lineWidth: 1
            },
            ticks: {
              color: tickColor,
              font: { size: isMobile ? 10 : 11 },
              autoSkip: true,
              maxTicksLimit: isMobile ? 6 : 12,
            },
            title: {
              display: true,
              text: 'Date',
              color: tickColor,
              font: { size: isMobile ? 11 : 12, weight: 'bold' }
            }
          },
          y: {
            position: 'left',
            grid: {
              color: gridColor,
              lineWidth: 1
            },
            ticks: {
              color: tickColor,
              font: { size: isMobile ? 10 : 11 },
              callback: function(value) {
                return `$${value}`;
              }
            },
            title: {
              display: true,
              text: 'Crude Oil Price ($/bbl)',
              color: tickColor,
              font: { size: isMobile ? 11 : 12, weight: 'bold' }
            }
          },
          y1: {
            type: 'linear',
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              color: tickColor,
              font: { size: isMobile ? 10 : 11 },
              callback: function(value) {
                return `$${value}`;
              }
            },
            title: {
              display: true,
              text: 'Natural Gas Price ($/MMBtu)',
              color: tickColor,
              font: { size: isMobile ? 11 : 12, weight: 'bold' }
            }
          }
        },
        interaction: {
          intersect: false,
          mode: undefined
        },
        onHover: (event: any, _activeElements: any, chart: any) => {
          const elements = chart.getElementsAtEventForMode(
            event,
            'nearest',
            { intersect: false, axis: 'xy' },
            false
          );
          const newHovered = elements.length > 0
            ? { datasetIndex: elements[0].datasetIndex, index: elements[0].index }
            : null;
          const prev = hoveredRef.current;
          const changed = (!prev && newHovered) || (prev && !newHovered) ||
            (prev && newHovered && (prev.datasetIndex !== newHovered.datasetIndex || prev.index !== newHovered.index));
          if (changed) {
            hoveredRef.current = newHovered;
            // Feed the tooltip's active elements so the vertical hover line plugin
            // (which reads tooltip._active) can draw, even though the tooltip is hidden.
            if (chart.tooltip) {
              if (newHovered) {
                chart.tooltip.setActiveElements(
                  [{ datasetIndex: newHovered.datasetIndex, index: newHovered.index }],
                  { x: event.x, y: event.y }
                );
              } else {
                chart.tooltip.setActiveElements([], { x: 0, y: 0 });
              }
            }
            chart.render();
          }
        },
        elements: {
          point: {
            hoverRadius: isMobile ? 0 : 6,
            hoverBorderWidth: 2,
            hitRadius: isMobile ? 0 : 20
          }
        }
      }
    };

    try {
      chartInstance.current = new Chart(ctx, config);
    } catch (error) {
      console.error('Chart creation error:', error);
    } finally {
      isCreating.current = false;
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      isCreating.current = false;
    };
  }, [processedData, rsColors, isMobile, isDark, tickColor, gridColor]);

  return <canvas style={{ minHeight: isMobile ? '350px' : '600px', height: isMobile ? '350px' : '600px' }} ref={chartRef} />;
};

export default EiaForecastChart;
